import { useState, useEffect, useCallback, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import {
  fetchAdminNotifications,
  markAdminNotificationAsRead,
} from "../services/adminNotificationService";
import {
  Bell,
  Info,
  CheckCircle,
  AlertTriangle,
  Mail,
  MailOpen,
  X,
  Loader2,
} from "lucide-react";

type Severity = "info" | "success" | "warn";

interface Message {
  id: string;
  severity: Severity;
  title: string;
  body: string;
  createdAt: string;
  tier: string;
  read: boolean;
  category: string;
}

interface NotificationsModalProps {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const SEVERITY_CONFIG = {
  info: {
    icon: Info,
    bgColor: "bg-blue-50",
    iconBgColor: "bg-blue-100",
    iconColor: "text-blue-600",
    textColor: "text-blue-900",
    borderColor: "ring-blue-200",
  },
  success: {
    icon: CheckCircle,
    bgColor: "bg-green-50",
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    textColor: "text-green-900",
    borderColor: "ring-green-200",
  },
  warn: {
    icon: AlertTriangle,
    bgColor: "bg-amber-50",
    iconBgColor: "bg-amber-100",
    iconColor: "text-amber-600",
    textColor: "text-amber-900",
    borderColor: "ring-amber-200",
  },
} as const;

export default function NotificationsModal({
  open,
  onClose,
  onUnreadCountChange,
}: NotificationsModalProps) {
  const [notifications, setNotifications] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());
  const [markingAll, setMarkingAll] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  useEffect(() => {
    if (!open) return;

    async function loadNotifications() {
      try {
        setLoading(true);
        setError(null);
        // tenant 300 hardcoded for now
        const response = await fetchAdminNotifications();

        if (response.success) {
          setNotifications(response.data);
        } else {
          throw new Error("Failed to load notifications");
        }
      } catch (err) {
        console.error("Error loading notifications:", err);
        setError(err instanceof Error ? err.message : "Failed to load notifications");
      } finally {
        setLoading(false);
      }
    }

    loadNotifications();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const markAsRead = async (id: string) => {
    if (markingIds.has(id)) return;

    setMarkingIds((prev) => new Set(prev).add(id));
    try {
      await markAdminNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    } finally {
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0 || markingAll) return;

    setMarkingAll(true);
    try {
      await Promise.all(
        unread.map(async (n) => {
          try {
            await markAdminNotificationAsRead(n.id);
            setNotifications((prev) =>
              prev.map((item) =>
                item.id === n.id ? { ...item, read: true } : item
              )
            );
          } catch (err) {
            console.error(`Failed to mark ${n.id} as read:`, err);
          }
        })
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const handleBackdrop = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
      onClick={handleBackdrop}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col ring-1 ring-slate-200"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[#E5E7EB] shrink-0">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#18365E]/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-[#18365E]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-[#18365E]">Notifications</h2>
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold text-white bg-[#FF6E4C]">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#717182] font-medium mt-0.5">
                  Use case intake reviews requiring attention
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={markingAll}
                className="hidden sm:inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-xs text-[#18365E] bg-[#F5F7FA] hover:bg-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#18365E] transition-all disabled:opacity-60"
              >
                {markingAll ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <MailOpen className="w-3.5 h-3.5" />
                )}
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg bg-[#F5F7FA] hover:bg-[#E5E7EB] transition-all flex items-center justify-center text-[#717182] hover:text-[#18365E] focus:outline-none focus:ring-2 focus:ring-[#18365E]"
              aria-label="Close notifications"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-xl ring-1 ring-[#e5e7eb] bg-white p-4 animate-pulse"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F5F7FA] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#F5F7FA] rounded w-3/4" />
                      <div className="h-3 bg-[#F5F7FA] rounded w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-xl ring-1 ring-red-200 bg-red-50 p-6 text-center">
              <Bell className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <h3 className="text-sm font-semibold text-red-900 mb-1">
                Failed to load notifications
              </h3>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="rounded-xl ring-1 ring-[#e5e7eb] bg-[#F5F7FA] p-10 text-center">
              <Bell className="w-8 h-8 text-[#18365E] mx-auto mb-3 opacity-60" />
              <h3 className="text-base font-semibold text-[#18365E] mb-1">
                You're all caught up
              </h3>
              <p className="text-sm text-[#717182] font-medium">
                New updates will appear here.
              </p>
            </div>
          )}

          {!loading &&
            !error &&
            notifications.map((notification) => {
              const config = SEVERITY_CONFIG[notification.severity];
              const Icon = config.icon;
              const isMarking = markingIds.has(notification.id);

              return (
                <div
                  key={notification.id}
                  className={`rounded-xl ring-1 ${config.borderColor} ${config.bgColor} p-4 transition-all ${
                    !notification.read ? "shadow-sm" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg ${config.iconBgColor} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className={`w-4 h-4 ${config.iconColor}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <h3 className={`text-sm font-semibold ${config.textColor} truncate`}>
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-[#FF6E4C] flex-shrink-0" />
                          )}
                        </div>
                      </div>

                      <p className={`text-xs ${config.textColor} opacity-90 mb-2 line-clamp-2`}>
                        {notification.body}
                      </p>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium opacity-75">
                          <span className={config.textColor}>
                            {formatDate(notification.createdAt)}
                          </span>
                          <span>•</span>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-md bg-white/50 ${config.textColor}`}
                          >
                            {notification.category}
                          </span>
                        </div>

                        {!notification.read ? (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            disabled={isMarking}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-[#18365E] bg-white/80 ring-1 ring-[#18365E]/15 hover:bg-white hover:ring-[#18365E]/40 focus:outline-none focus:ring-2 focus:ring-[#18365E] transition-all disabled:opacity-60"
                          >
                            {isMarking ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <MailOpen className="w-3 h-3" />
                            )}
                            Mark as read
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#717182]">
                            <Mail className="w-3 h-3" />
                            Read
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>,
    document.body
  );
}
